import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, X, ExternalLink, BadgeCheck, FileText, MessageCircle } from "lucide-react";
import { fmtDate, fmtKm, scoreColor, PROVIDER_STATUS, TOW_STATUS } from "../lib/labels";
import { api, photoUrl } from "../api";
import { inputCls, labelCls } from "./detail/SectionCard";

export function Spinner() {
  return (
    <div className="p-12 flex justify-center">
      <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

export function AdminStats({ stats }) {
  if (stats === null) return <Spinner />;
  return (
    <div data-testid="admin-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-5">
      {[
        { label: "Toplam Kullanıcı", value: stats.users },
        { label: "Kayıtlı Araç", value: stats.vehicles },
        { label: "Toplam Sorgu", value: stats.queries },
        { label: "Ortalama Skor", value: stats.avg_score },
      ].map((s) => (
        <div key={s.label} className="rounded-2xl border border-white/10 bg-slate-900/60 p-6">
          <p className="text-xs uppercase tracking-wider text-slate-500">{s.label}</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-cyan-300">{s.value}</p>
        </div>
      ))}
    </div>
  );
}

export function AdminVehiclesTable({ vehicles, onAdd, onEdit, onDelete }) {
  return (
    <div>
      <button data-testid="admin-add-vehicle-button" onClick={onAdd}
        className="mb-5 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-400 text-slate-950 font-semibold hover:bg-cyan-300 transition-all text-sm">
        <Plus className="w-4 h-4" /> Araç Kaydı Ekle
      </button>
      <div className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden">
        {vehicles === null ? <Spinner /> : (
          <div className="overflow-x-auto">
            <table data-testid="admin-vehicles-table" className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-4">Şasi No</th>
                  <th className="px-5 py-4">Araç</th>
                  <th className="px-5 py-4">Plaka</th>
                  <th className="px-5 py-4">KM</th>
                  <th className="px-5 py-4">Kaynak</th>
                  <th className="px-5 py-4">Skor</th>
                  <th className="px-5 py-4 text-right">İşlem</th>
                </tr>
              </thead>
              <tbody>
                {vehicles.map((v) => (
                  <tr key={v.id} className="border-b border-white/5 hover:bg-slate-800/40 transition-colors">
                    <td className="px-5 py-3.5 font-mono-vin text-cyan-300 text-xs">{v.vin}</td>
                    <td className="px-5 py-3.5 text-slate-200">{v.brand} {v.model} {v.year}</td>
                    <td className="px-5 py-3.5 text-slate-400">{v.plate || "-"}</td>
                    <td className="px-5 py-3.5 text-slate-400">{fmtKm(v.km)}</td>
                    <td className="px-5 py-3.5">
                      <span className="text-xs px-2.5 py-1 rounded-full border border-white/10 text-slate-300">
                        {v.source === "user" ? (v.owner_email || "Kullanıcı") : v.source === "admin" ? "Yönetici" : "Sistem"}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 font-bold" style={{ color: scoreColor(v.score) }}>{v.score}</td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex justify-end gap-2">
                        <Link data-testid={`admin-view-${v.vin}`} to={`/rapor/${v.vin}`}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                          <ExternalLink className="w-4 h-4" />
                        </Link>
                        <button data-testid={`admin-edit-vehicle-${v.vin}`} onClick={() => onEdit(v)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`admin-delete-vehicle-${v.vin}`} onClick={() => onDelete(v)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminUsersTable({ users, onDelete }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden">
      {users === null ? <Spinner /> : (
        <div className="overflow-x-auto">
          <table data-testid="admin-users-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-4">Ad</th>
                <th className="px-5 py-4">E-posta</th>
                <th className="px-5 py-4">Rol</th>
                <th className="px-5 py-4">Kayıt Tarihi</th>
                <th className="px-5 py-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-white/5 hover:bg-slate-800/40 transition-colors">
                  <td className="px-5 py-3.5 text-slate-200">{u.name}</td>
                  <td className="px-5 py-3.5 text-slate-300">{u.email}</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs px-2.5 py-1 rounded-full border ${u.role === "admin" ? "border-amber-500/40 bg-amber-500/10 text-amber-300" : "border-white/10 text-slate-300"}`}>
                      {u.role === "admin" ? "Yönetici" : "Kullanıcı"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-400">{fmtDate(u.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    {u.role !== "admin" && (
                      <button data-testid={`admin-delete-user-${u.email}`} onClick={() => onDelete(u)}
                        className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminQueriesTable({ queries, onDelete }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden">
      {queries === null ? <Spinner /> : (
        <div className="overflow-x-auto">
          <table data-testid="admin-queries-table" className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-4">Şasi No</th>
                <th className="px-5 py-4">Araç</th>
                <th className="px-5 py-4">Kullanıcı</th>
                <th className="px-5 py-4">Skor</th>
                <th className="px-5 py-4">Tarih</th>
                <th className="px-5 py-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {queries.map((q) => (
                <tr key={q.id} className="border-b border-white/5 hover:bg-slate-800/40 transition-colors">
                  <td className="px-5 py-3.5 font-mono-vin text-cyan-300 text-xs">{q.vin}</td>
                  <td className="px-5 py-3.5 text-slate-200">{q.vehicle}</td>
                  <td className="px-5 py-3.5 text-slate-400">{q.user_email || "Anonim"}</td>
                  <td className="px-5 py-3.5 font-bold" style={{ color: scoreColor(q.score) }}>{q.score}</td>
                  <td className="px-5 py-3.5 text-slate-400 whitespace-nowrap">{fmtDate(q.created_at)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <button data-testid={`admin-delete-query-${q.id}`} onClick={() => onDelete(q)}
                      className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AdminVehicleFormModal({ vehicleForm, setVehicleForm, onSubmit, onClose }) {
  const setField = (k, val) => setVehicleForm((f) => ({ ...f, data: { ...f.data, [k]: val } }));
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="admin-vehicle-form-modal" className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-slate-900 border border-white/10 shadow-2xl p-6 sm:p-8"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{vehicleForm.mode === "add" ? "Araç Kaydı Ekle" : "Araç Kaydını Düzenle"}</h2>
          <button data-testid="admin-vehicle-form-close" onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className={labelCls}>Şasi No / VIN *</label>
            <input data-testid="admin-vehicle-form-vin" required disabled={vehicleForm.mode === "edit"}
              value={vehicleForm.data.vin}
              onChange={(e) => setField("vin", e.target.value.toUpperCase())}
              className={`${inputCls} font-mono-vin tracking-widest disabled:opacity-50`} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Marka *</label>
              <input data-testid="admin-vehicle-form-brand" required value={vehicleForm.data.brand}
                onChange={(e) => setField("brand", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Model *</label>
              <input data-testid="admin-vehicle-form-model" required value={vehicleForm.data.model}
                onChange={(e) => setField("model", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Yıl *</label>
              <input data-testid="admin-vehicle-form-year" required type="number" min="1950" max="2027" value={vehicleForm.data.year}
                onChange={(e) => setField("year", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Plaka</label>
              <input data-testid="admin-vehicle-form-plate" value={vehicleForm.data.plate}
                onChange={(e) => setField("plate", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Kilometre</label>
              <input data-testid="admin-vehicle-form-km" type="number" min="0" value={vehicleForm.data.km}
                onChange={(e) => setField("km", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Motor</label>
              <input data-testid="admin-vehicle-form-engine" value={vehicleForm.data.engine}
                onChange={(e) => setField("engine", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Yakıt</label>
              <input data-testid="admin-vehicle-form-fuel" value={vehicleForm.data.fuel}
                onChange={(e) => setField("fuel", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Renk</label>
              <input data-testid="admin-vehicle-form-color" value={vehicleForm.data.color}
                onChange={(e) => setField("color", e.target.value)} className={inputCls} />
            </div>
          </div>
          <button data-testid="admin-vehicle-form-submit" type="submit"
            className="w-full py-3 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 transition-all">
            {vehicleForm.mode === "add" ? "Kaydı Ekle" : "Kaydı Güncelle"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function AdminProviders({ kind, items, onAction }) {
  const [catalog, setCatalog] = useState([]);
  useEffect(() => { api.get("/isletme-turleri").then((r) => setCatalog(r.data)).catch(() => {}); }, []);
  const typeLabel = (k) => catalog.find((c) => c.key === k)?.label || "";
  return (
    <div data-testid={`admin-providers-${kind}`} className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden">
      {items === null ? <Spinner /> : items.length === 0 ? (
        <p className="p-10 text-sm text-slate-500 text-center">Kayıtlı başvuru yok.</p>
      ) : (
        <div className="divide-y divide-white/5">
          {items.map((p) => {
            const st = PROVIDER_STATUS[p.status || "onayli"] || PROVIDER_STATUS.bekliyor;
            const pk = p._kind || kind;
            const name = p.business_name || p.company_name || "(İsimsiz başvuru)";
            return (
              <div key={p.id} data-testid={`provider-${pk}-${p.user_id}`}
                className="p-5 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.logo && <img src={photoUrl(p.logo)} alt="" className="w-8 h-8 rounded-lg object-cover border border-white/10" />}
                    <p className="font-semibold text-slate-100">{name}</p>
                    {p.verified && (
                      <span data-testid={`provider-verified-${p.user_id}`} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 font-semibold">
                        <BadgeCheck className="w-3 h-3" /> Doğrulanmış İşletme
                      </span>
                    )}
                    {typeLabel(p.business_type) && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300">{typeLabel(p.business_type)}</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5">
                    {p.owner_name} • {p.owner_email} • {p.phone || "-"}
                  </p>
                  {(p.tax_number || p.authorized_person) && (
                    <p className="text-xs text-slate-500 mt-1">
                      {p.tax_number && `Vergi No: ${p.tax_number}`}{p.tax_number && p.authorized_person && " • "}{p.authorized_person && `Yetkili: ${p.authorized_person}`}
                    </p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    {p.city || "-"}{p.district && ` / ${p.district}`}{p.address && ` — ${p.address}`}
                  </p>
                  {p.service_types && p.service_types.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {p.service_types.map((t) => (
                        <span key={t} className="text-[10px] px-2 py-0.5 rounded-full border border-white/10 text-slate-400">{t}</span>
                      ))}
                    </div>
                  )}
                  {p.regions && p.regions.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1.5">Bölgeler: {p.regions.join(", ")}</p>
                  )}
                  <p className="text-xs text-slate-500 mt-1">
                    {p.is_247 ? "7/24 hizmet" : (p.working_hours || "")}
                    {p.working_days && p.working_days.length > 0 && ` • ${p.working_days.join(", ")}`}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5">
                    {p.tax_document && (
                      <a data-testid={`provider-taxdoc-${p.user_id}`} href={photoUrl(p.tax_document)} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200">
                        <FileText className="w-3.5 h-3.5" /> Vergi Levhası
                      </a>
                    )}
                    <span className="text-xs text-slate-600">Başvuru: {fmtDate(p.created_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap shrink-0">
                  <span className={`text-xs px-3 py-1.5 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
                  {p.status !== "onayli" && (
                    <button data-testid={`provider-approve-${p.user_id}`} onClick={() => onAction(pk, p.user_id, "onayla")}
                      className="px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/20 transition-colors">
                      Onayla
                    </button>
                  )}
                  {(!p.status || p.status === "bekliyor") && (
                    <button data-testid={`provider-reject-${p.user_id}`} onClick={() => onAction(pk, p.user_id, "red")}
                      className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-red-400/50 hover:text-red-300 transition-colors">
                      Reddet
                    </button>
                  )}
                  {p.status === "onayli" && !p.verified && (
                    <button data-testid={`provider-verify-${p.user_id}`} onClick={() => onAction(pk, p.user_id, "dogrula")}
                      className="px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs font-bold hover:bg-emerald-500/20 transition-colors">
                      Doğrula
                    </button>
                  )}
                  {p.status === "onayli" && p.verified && (
                    <button data-testid={`provider-unverify-${p.user_id}`} onClick={() => onAction(pk, p.user_id, "dogrulama_kaldir")}
                      className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-amber-400/50 hover:text-amber-300 transition-colors">
                      Rozeti Kaldır
                    </button>
                  )}
                  {p.status === "onayli" && (
                    <button data-testid={`provider-disable-${p.user_id}`} onClick={() => onAction(pk, p.user_id, "pasif")}
                      className="px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-xs hover:border-amber-400/50 hover:text-amber-300 transition-colors">
                      Pasifleştir
                    </button>
                  )}
                  {p.status === "pasif" && (
                    <button data-testid={`provider-enable-${p.user_id}`} onClick={() => onAction(pk, p.user_id, "aktif")}
                      className="px-4 py-2 rounded-lg bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 text-xs font-bold hover:bg-cyan-400/20 transition-colors">
                      Aktifleştir
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AdminRequests({ data }) {
  if (data === null) return <Spinner />;
  const Table = ({ items, kind, testid }) => (
    <div className="rounded-2xl border border-white/10 bg-slate-900/60 overflow-hidden">
      <div className="overflow-x-auto">
        <table data-testid={testid} className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3.5">Araç</th>
              <th className="px-5 py-3.5">{kind === "cekici" ? "Bölge" : "Servis"}</th>
              <th className="px-5 py-3.5">Talep Eden</th>
              <th className="px-5 py-3.5">Durum</th>
              <th className="px-5 py-3.5">Tarih</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500 text-sm">Talep yok.</td></tr>
            ) : items.map((r) => {
              const st = TOW_STATUS[r.status] || TOW_STATUS.bekliyor;
              return (
                <tr key={r.id} className="border-b border-white/5 hover:bg-slate-800/40 transition-colors">
                  <td className="px-5 py-3 text-slate-200">{[r.brand, r.model].filter(Boolean).join(" ") || "-"}{r.plate && ` (${r.plate})`}{r.vin && <span className="block text-[10px] font-mono-vin text-cyan-300/70">{r.vin}</span>}</td>
                  <td className="px-5 py-3 text-slate-400">{kind === "cekici" ? `${r.city} — ${r.address}` : (r.business_name || "-")}</td>
                  <td className="px-5 py-3 text-slate-400">{r.user_name || r.user_email}</td>
                  <td className="px-5 py-3"><span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${st.cls}`}>{st.label}</span></td>
                  <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
  return (
    <div data-testid="admin-requests" className="space-y-6">
      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Çekici Talepleri ({data.cekici.length})</h3>
        <Table items={data.cekici} kind="cekici" testid="admin-tow-requests-table" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-slate-300 mb-3">Servis Talepleri ({data.servis.length})</h3>
        <Table items={data.servis} kind="servis" testid="admin-service-requests-table" />
      </div>
    </div>
  );
}


import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export function AdminVisitorStats({ stats }) {
  if (!stats) return <Spinner />;
  const cards = [
    { label: "Şu An Aktif", value: stats.aktif, testid: "stat-aktif" },
    { label: "Bugünkü Ziyaretçi", value: stats.bugun, testid: "stat-bugun" },
    { label: "Son 7 Gün", value: stats.son7, testid: "stat-son7" },
    { label: "Son 30 Gün", value: stats.son30, testid: "stat-son30" },
    { label: "Toplam Ziyaretçi", value: stats.toplam, testid: "stat-toplam" },
    { label: "Kayıtlı Kullanıcı", value: stats.kayitli_kullanici, testid: "stat-kayitli" },
    { label: "Yeni Kayıt (30 Gün)", value: stats.yeni_kayit_30gun, testid: "stat-yeni-kayit" },
    { label: "Teklif Oluşturan", value: stats.teklif_olusturan, testid: "stat-teklif" },
  ];
  const chartData = (stats.gunluk || []).map((d) => ({ ...d, gun: d.date.slice(5).split("-").reverse().join(".") }));
  return (
    <div data-testid="admin-visitor-stats" className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.testid} data-testid={c.testid} className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">{c.label}</p>
            <p className="text-2xl font-bold text-cyan-300 mt-1">{c.value}</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">Günlük Ziyaretçiler (Son 30 Gün)</h3>
        {chartData.length === 0 ? (
          <p className="text-xs text-slate-500">Henüz veri yok — ziyaretler geldikçe grafik oluşur.</p>
        ) : (
          <div data-testid="visitor-chart" className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.15)" />
                <XAxis dataKey="gun" tick={{ fill: "#94a3b8", fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} allowDecimals={false} width={30} />
                <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} labelStyle={{ color: "#e2e8f0" }} />
                <Bar dataKey="visitors" name="Tekil Ziyaretçi" fill="#f97316" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="rounded-xl border border-white/10 bg-slate-900/60 p-4">
        <h3 className="text-sm font-semibold text-slate-200 mb-3">En Çok Ziyaret Edilen Sayfalar</h3>
        {(!stats.top_sayfalar || stats.top_sayfalar.length === 0) ? (
          <p className="text-xs text-slate-500">Henüz veri yok.</p>
        ) : (
          <div className="space-y-1.5">
            {stats.top_sayfalar.map((p) => (
              <div key={p.path} data-testid={`top-page-${p.path.replace(/\W/g, "") || "home"}`}
                className="flex items-center justify-between text-xs border-b border-white/5 pb-1.5">
                <span className="text-slate-300 font-mono">{p.path}</span>
                <span className="text-slate-500">{p.views} görüntülenme • {p.visitors} tekil</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export const BIZ_GROUPS = [
  { key: "galeriler", label: "Galeriler" },
  { key: "cekiciler", label: "Çekiciler" },
  { key: "servisler", label: "Tamirciler / Servisler" },
  { key: "cam_filmi", label: "Cam Filmciler" },
  { key: "yedek_parca", label: "Yedek Parçacılar" },
  { key: "diger", label: "Diğer İşletmeler" },
];

export function bizGroupOf(p) {
  const bt = p.business_type || "";
  if (bt === "galeri") return "galeriler";
  if (bt === "seramik") return "cam_filmi";
  if (bt === "yedek_parca") return "yedek_parca";
  if (["cekici", "arac_tasima", "agir_vasita"].includes(bt)) return "cekiciler";
  if (["filo", "aksesuar", "sigorta"].includes(bt)) return "diger";
  if (bt) return "servisler";
  return p._kind === "cekici" ? "cekiciler" : "servisler";
}

export function AdminBusinesses({ providers, onAction }) {
  const [group, setGroup] = useState("hepsi");
  if (providers === null) return <Spinner />;
  const items = [
    ...(providers.servisler || []).map((p) => ({ ...p, _kind: "servis" })),
    ...(providers.cekiciler || []).map((p) => ({ ...p, _kind: "cekici" })),
  ];
  const counts = {};
  items.forEach((p) => { const g = bizGroupOf(p); counts[g] = (counts[g] || 0) + 1; });
  const pending = items.filter((p) => (p.status || "onayli") !== "onayli");
  const filtered = group === "hepsi" ? items : items.filter((p) => bizGroupOf(p) === group);
  return (
    <div data-testid="admin-isletmeler" className="space-y-6">
      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-amber-300 mb-3">Onay Bekleyenler ({pending.length})</h3>
          <AdminProviders kind="servis" items={pending} onAction={onAction} />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {[{ key: "hepsi", label: "Tümü", count: items.length }, ...BIZ_GROUPS.map((g) => ({ ...g, count: counts[g.key] || 0 }))].map((g) => (
          <button key={g.key} data-testid={`biz-filter-${g.key}`} onClick={() => setGroup(g.key)}
            className={`px-4 py-2 rounded-xl border text-xs font-semibold transition-colors ${
              group === g.key ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-slate-200"
            }`}>
            {g.label} ({g.count})
          </button>
        ))}
      </div>
      <AdminProviders kind="servis" items={filtered} onAction={onAction} />
    </div>
  );
}

const QS_PERIODS = [
  { key: "bugun", label: "Bugün" },
  { key: "hafta", label: "Bu Hafta" },
  { key: "ay", label: "Bu Ay" },
  { key: "3ay", label: "Son 3 Ay" },
  { key: "yil", label: "Bu Yıl" },
  { key: "tum", label: "Tüm Zamanlar" },
];

export function AdminQuoteStats({ stats, period, onPeriod }) {
  return (
    <div data-testid="admin-quote-stats" className="rounded-2xl border border-white/10 bg-slate-900/60 p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-100">Teklif İstatistikleri</h2>
        <div className="flex flex-wrap gap-2">
          {QS_PERIODS.map((p) => (
            <button key={p.key} data-testid={`qs-period-${p.key}`} onClick={() => onPeriod(p.key)}
              className={`px-3.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                period === p.key ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-300" : "border-white/10 text-slate-400 hover:text-slate-200"
              }`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>
      {stats === null ? <Spinner /> : (
        <>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Toplam Teklif", value: stats.toplam, testid: "qs-total" },
              { label: "Bireysel Kullanıcı", value: stats.bireysel, testid: "qs-bireysel" },
              { label: "Kurumsal Kullanıcı", value: stats.kurumsal, testid: "qs-kurumsal" },
            ].map((c) => (
              <div key={c.testid} data-testid={c.testid} className="rounded-xl border border-white/10 bg-slate-950/50 p-4">
                <p className="text-[11px] text-slate-500 uppercase tracking-wider">{c.label}</p>
                <p className="text-2xl font-bold text-cyan-300 mt-1">{c.value}</p>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-3">İşletme Türüne Göre Teklifler</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {stats.gruplar.map((g) => (
                <div key={g.key} data-testid={`qs-group-${g.key}`} className="rounded-xl border border-white/10 bg-slate-950/50 p-3.5">
                  <p className="text-[11px] text-slate-500">{g.label}</p>
                  <p className="text-xl font-bold text-slate-100 mt-1">{g.count}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-300 mb-3">İşletme Bazında Ulaşan Talepler</h3>
            {stats.isletmeler.length === 0 ? (
              <p data-testid="qs-biz-empty" className="text-xs text-slate-500">
                Bu dönemde işletme bazlı kayıt yok. İşletmeye ulaşan talep takibi bu özelliğin aktifleştirildiği tarihten itibaren tutulur; geçmiş talepler tür bazlı özetlerde yer alır.
              </p>
            ) : (
              <div className="rounded-xl border border-white/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table data-testid="qs-biz-table" className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-3">İşletme</th>
                        <th className="px-4 py-3">Tür</th>
                        <th className="px-4 py-3">Grup</th>
                        <th className="px-4 py-3 text-right">Ulaşan Talep</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.isletmeler.map((b) => (
                        <tr key={b.user_id} data-testid={`qs-biz-${b.user_id}`} className="border-b border-white/5">
                          <td className="px-4 py-2.5 text-slate-200">{b.name || "-"}</td>
                          <td className="px-4 py-2.5 text-slate-400">{b.type_label || "-"}</td>
                          <td className="px-4 py-2.5 text-slate-400">{b.group_label}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-cyan-300">{b.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const FB_STATUS = {
  yeni: { label: "Yeni", cls: "border-amber-500/40 bg-amber-500/10 text-amber-300" },
  inceleniyor: { label: "İnceleniyor", cls: "border-cyan-500/40 bg-cyan-500/10 text-cyan-300" },
  cozuldu: { label: "Çözüldü", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" },
  kapatildi: { label: "Kapatıldı", cls: "border-white/10 text-slate-500" },
};
const FB_ROLE = { user: "Bireysel", servis: "İşletme (Servis)", cekici: "İşletme (Çekici)", admin: "Admin" };

export function AdminFeedback({ items, onStatus }) {
  const [detail, setDetail] = useState(null);
  if (items === null) return <Spinner />;
  return (
    <div data-testid="admin-feedback" className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">Geri bildirim yok.</p>
      ) : items.map((f) => {
        const st = FB_STATUS[f.status] || FB_STATUS.yeni;
        return (
          <div key={f.id} data-testid={`feedback-row-${f.id}`}
            className="rounded-xl border border-white/10 bg-slate-900/60 px-4 py-3.5 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-100">{f.record_no} — {f.category_label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{f.user_name || f.user_email} • {FB_ROLE[f.user_role] || f.user_role} • {fmtDate(f.created_at)}</p>
              <p className="text-xs text-slate-400 mt-1 line-clamp-1">{f.message}</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span data-testid={`feedback-status-${f.id}`} className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${st.cls}`}>{st.label}</span>
              <button data-testid={`feedback-detail-${f.id}`} onClick={() => setDetail(f)}
                className="text-xs px-3 py-1.5 rounded-lg border border-cyan-400/40 text-cyan-300 hover:bg-cyan-400/10 transition-colors">Detay</button>
            </div>
          </div>
        );
      })}
      {detail && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDetail(null)}>
          <div data-testid="feedback-detail-modal" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-2xl bg-slate-900 border border-white/10 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{detail.record_no} — {detail.category_label}</h3>
              <button data-testid="feedback-detail-close" onClick={() => setDetail(null)} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-xs text-slate-500 space-y-1 mb-4">
              <p>Gönderen: {detail.user_name || "-"} ({detail.user_email})</p>
              <p>Kullanıcı Türü: {FB_ROLE[detail.user_role] || detail.user_role}</p>
              <p>Tarih: {fmtDate(detail.created_at)}</p>
            </div>
            <p data-testid="feedback-detail-message" className="text-sm text-slate-200 whitespace-pre-wrap rounded-xl border border-white/10 bg-slate-950/50 p-4">{detail.message}</p>
            {detail.photos && detail.photos.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {detail.photos.map((p) => <img key={p} src={photoUrl(p)} alt="" className="w-24 h-16 object-cover rounded-lg border border-white/10" />)}
              </div>
            )}
            <div className="mt-5">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Durum Değiştir</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(FB_STATUS).map(([k, v]) => (
                  <button key={k} data-testid={`feedback-set-${k}`}
                    onClick={() => { onStatus(detail.id, k); setDetail((d) => ({ ...d, status: k })); }}
                    className={`text-xs px-3.5 py-2 rounded-lg border font-semibold transition-colors ${detail.status === k ? v.cls : "border-white/10 text-slate-400 hover:text-slate-200"}`}>
                    {v.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
